
import mixpanel from 'mixpanel-browser';

import { fromVoiceId } from '../../src/voices.js';
import { timeOperation } from '../../src/common.js';

export const PROD_HOSTS = ['voicetopics.com', 'www.voicetopics.com', 'voicetopics.xyz', 'www.voicetopics.xyz'];

export function* range(n, start = 0) {
  for (let i = start; i < n + start; ++i) yield i;
}

export function createId({ bytes, prefix } = {}) {
  bytes ||= 6;
  return (prefix ? prefix + '-' : '') +
      Array.from(range(bytes * 2))
          .map(() => Math.floor(Math.random() * 16).toString(16))
          .join('');
}

export function buildHeaders({ impersonationUser, contentType = null } = {}) {
  const headers = {};
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  if (impersonationUser) {
    headers.impersonate = impersonationUser;
  }
  return headers;
}

export function listenAllEvents(source, listener) {
  for (const key in source) {
    if (/^on/.test(key)) {
      source.addEventListener(key.substr(2), listener);
    }
  }
}

export async function createAudio(buffer) {
  const audioContext = new AudioContext();
  const audioBuffer = await audioContext.decodeAudioData(buffer);
  const audioSource = audioContext.createBufferSource();
  audioSource.buffer = audioBuffer;
  audioSource.connect(audioContext.destination);
  return audioSource;
}

export function toTitleCase(str) {
  if (!str) return;

  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

export async function fetchOrganizations(userInfo, impersonationUser) {
  if (!userInfo) return;

  const [loadTime, response] = await timeOperation(() => fetch('/api/me/organizations', {
    headers: buildHeaders({ impersonationUser }),
  }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading organizations', { message: errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function completeNewUserChecklistItem(userInfo, setUserInfo, impersonationUser, flag, value) {
  if (!userInfo || userInfo.newUserFlags?.[flag]) return;

  const response = await fetch('/api/me/completeNewUserChecklistItem', {
    method: 'POST',
    headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
    body: JSON.stringify({ flag, value }),
  });

  if (response.ok) {
    setUserInfo(await response.json());
  } else {
    throw Error(await response.text());
  }
}

export async function fetchOrganization(userInfo, impersonationUser, id) {
  if (!userInfo) return null;

  const [loadTime, response] = await timeOperation(() =>
      fetch(`/api/organization/${id}`, { headers: buildHeaders({ impersonationUser }) }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading organization', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export function downloadCsv(csvData, filename) {
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const elem = document.createElement('a');
  elem.href = url;
  elem.setAttribute('download', filename);
  elem.click();

  URL.revokeObjectURL(url);
}

export async function createOrganization(userInfo, impersonationUser) {
  const [loadTime, response] = await timeOperation(() => fetch('/api/organization', {
    method: 'PUT',
    headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
    body: JSON.stringify({ name: `${userInfo.displayName || userInfo.email}'s Organization` }),
  }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error creating organization', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function deleteOrganization(impersonationUser, organizationId, hardDelete) {
  const [loadTime, response] = await timeOperation(() =>
      fetch(`/api/organization/${organizationId}/delete`, {
        method: 'POST',
        headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
        body: JSON.stringify({ hardDelete }),
      }));

  if (!response.ok) {
    const errorMessage = await response.text();
    mixpanel.track('Error deleting organization', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

// Updates the current user's membership
export async function updateMembership(impersonationUser, organizationId, status) {
  const [loadTime, response] = await timeOperation(() => fetch(`/api/organization/${organizationId}/member`, {
    method: 'POST',
    headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
    body: JSON.stringify({ status }),
  }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error updating organization invite', { loadTime, errorMessage });
    throw Error(errorMessage);
  }
};

// Updates all members of an organization
export async function updateMembers({
  organizationId,
  members,
  impersonationUser,
}) {
  const response = await fetch(`/api/organization/${organizationId}/members`, {
    method: 'POST',
    headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
    body: JSON.stringify({ members }),
  });
  if (!response.ok) {
    throw Error(await response.text());
  }
}

export function updateOrganization({ organizations, setOrganizations, selectedOrganization, setSelectedOrganization }) {
  return (newOrganization) => {
    if (selectedOrganization.id === newOrganization.id) {
      setSelectedOrganization(newOrganization);
    }

    setOrganizations(organizations.map(organization => organization.id === newOrganization.id ? newOrganization : organization));
  };
}

export function blurOnEnter(event) {
  event.stopPropagation();
  if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Escape') {
    event.preventDefault();
    event.target.blur();
  }
}

export function actionOrCancel(action, cancel) {
  return (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      return action(event);
    } else if (event.key === 'Escape') {
      return cancel(event);
    }
  };
}

export function adminCheck(userInfo, organization) {
  const memberInfo = userInfo && organization &&
      organization.members.find(member => member.email === userInfo.email);
  return memberInfo && memberInfo.admin && memberInfo.status === 'ACCEPTED';
}

// - Returns { unauthenticated: true, ... } when the user is not logged in (gets a 404 from /api/me)
// - Returns { unauthorized: true, ... } when the user is logged in, but does not exist in the DB
// - Returns full user data otherwise
export async function fetchUserInfo(impersonationUser, setImpersonationUser) {
  const response = await fetch('/api/me', {
    headers: buildHeaders({ impersonationUser }),
  })

  if (response.ok) {
    return await response.json();
  } else if (response.status === 400 && impersonationUser) {
    setImpersonationUser?.(false);
    return null;
  } else {
    return { unauthenticated: true };
  }
}

export async function fetchContent(contentId, impersonationUser) {
  const [loadTime, response] =
      await timeOperation(() => fetch(`/api/content/${contentId}`, {
        headers: buildHeaders({ impersonationUser })
      }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading content', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function fetchAd(adId, impersonationUser) {
  const [loadTime, response] =
      await timeOperation(() => fetch(`/api/ads/${adId}`, {
        headers: buildHeaders({ impersonationUser })
      }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading ad', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function fetchVersions(contentId, impersonationUser) {
  const [loadTime, response] =
      await timeOperation(() => fetch(`/api/content/${contentId}/versions`, {
        headers: buildHeaders({ impersonationUser })
      }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading versions', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function executeNotificationAction({
      notification,
      navigateRef,
      organizations,
      selectedOrganization,
      setSelectedOrganization,
    }) {
  // Change the selected organization if needed
  const newOrganizationId = notification.data?.oragnizationId;
  if (newOrganizationId && newOrganizationId !== selectedOrganization.id) {
    const newOrganization = organizations.find(o => o.id === notification.action?.organization);
    if (newOrganization) {
      setSelectedOrganization(newOrganization);
    }
  }

  if (notification.action?.path) {
    navigateRef.current(notification.action.path);
  }
}

export async function recordAudio({ voiceId, input, organization }) {
  const { voice, source } = fromVoiceId(voiceId);
  const [loadTime, response] = await timeOperation(() =>
      fetch(`/api/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice, input, organization, source }),
      }));
  if (response.ok) {
    const audioBuffer = await response.arrayBuffer();
    return await createAudio(audioBuffer);
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error recording audio', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function fetchAudio(url) {
  const [loadTime, response] = await timeOperation(() => fetch(url, { headers: { Origin: '*' } }));
  if (response.ok) {
    const audioBuffer = await response.arrayBuffer();
    return await createAudio(audioBuffer);
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error fetching audio', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export function renderDuration(duration) {
  if (!duration && duration !== 0) return '-:--';

  const hours = Math.floor(duration / 60 / 60);
  const minutes = Math.floor(duration / 60 % 60)
  const seconds = Math.floor(duration % 60);
  if (hours) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`;
  }
}

export async function fetchUsageLimit(organization, impersonationUser) {
  const response = await fetch(`/api/organization/${organization.id}/usageLimit`,
      { headers: buildHeaders({ impersonationUser }) });
  if (response.ok) {
    return await response.json();
  } else {
    throw Error(await response.text());
  }
}

export async function fetchVoices({ organization, impersonationUser, allLanguages = false, allSources = false, ignoreBlacklist = false }) {
  if (!organization) throw Error('Missing organization');
  const queryParams = [];
  if (allLanguages) queryParams.push('allLanguages=true');
  if (allSources) queryParams.push('allSources=true');
  if (ignoreBlacklist) queryParams.push('ignoreBlacklist=true');
  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

  const [loadTime, response] = await timeOperation(() => fetch(
      `/api/organization/${organization.id}/voices${queryString}`,
      {
        headers: buildHeaders({ impersonationUser }),
      }));

  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    mixpanel.track('Error loading voices', { message: errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export async function fetchLexicon({ organizationId, impersonationUser, params }) {
  if (!organizationId) throw Error('Missing organizationId');

  const response = await fetch(`/api/organization/${organizationId}/lexicon`, {
    method: 'POST',
    headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
    body: JSON.stringify(params),
  });
  if (response.ok) {
    return await response.json();
  } else {
    const errorMessage = await response.text();
    throw new Error(errorMessage);
  }
}

export async function markRead(notification, setMessage, impersonationUser) {
  const response =
      await fetch(`/api/notifications/${notification.eventTime}/read`, {
        method: 'POST',
        headers: buildHeaders({ impersonationUser, contentType: 'application/json' })
      });
  if (!response.ok) {
    setMessage?.({ children: await response.text(), severity: 'error' });
  }
};

export function newlinesToBreaks(text) {
  const parts = text.split("\n");
  for (let index = parts.length - 1; index > 0; --index) {
    parts.splice(index, 0, <br key={`break-${index}`} />);
  }
  return parts;
}

export function urlWith(url, newProperties) {
  let newUrl = new URL(url);
  for (let property of ['protocol', 'password', 'username', 'host', 'pathname', 'search', 'hash']) {
    if (newProperties[property] || newProperties[property] === '') {
      newUrl[property] = newProperties[property];
    }
  }
  return newUrl;
}

export function paramsWith(searchParams, newParams) {
  let newSearchParams = new URLSearchParams(searchParams);
  for (let [key, value] of Object.entries(newParams)) {
    if (value) {
      newSearchParams.set(key, value);
    } else {
      newSearchParams.delete(key);
    }
  }
  return newSearchParams;
}

export async function logout() {
  await fetch('/logout');

  const newLocation = urlWith(
      window.location,
      {
        pathname: '/',
        hash: '/',
        search: '',
      });
  console.log(`Logout, navigate to ${newLocation.toString()}`);
  window.location.href = newLocation.toString();
}

export function isValidUrl(s) {
  try {
    new URL(s);
    return true;
  } catch(e) { // eslint-disable-line no-unused-vars
    return false;
  }
}

export function locationEquals(a, b) {
  return a.pathname === b.pathname && a.search === b.search && a.hash === b.hash;
}

export function lexiconEq(a, b) {
  a ||= [];
  b ||= [];
  if (a.length !== b.length) return false;
  return a.every(aElem => b.some(bElem =>
      aElem.target === bElem.target
          && aElem.replacement=== bElem.replacement
          && aElem.caseSensitive === bElem.caseSensitive));
}

export async function requestReprocessing(contentId, options = {}) {
  const { impersonationUser, versionId } = options;

  const [loadTime, response] = await timeOperation(() =>
    fetch(`/api/content/${contentId}/retry`, {
      method: 'POST',
      headers: buildHeaders({ impersonationUser, contentType: 'application/json' }),
      body: JSON.stringify({ versionId }),
    })
  );

  if (!response.ok) {
    const errorMessage = await response.text();
    mixpanel.track('Error requesting reprocessing', { errorMessage, loadTime });
    throw Error(errorMessage);
  }
}

export function getAuthUrl() {
  // NOTE: authUrl inherits queryParam originalTarget from window.location
  let url = urlWith(
      window.location,
      {
        pathname: '/auth/google',
        hash: '',
      });
  // Rewrite localhost port so the local dev server can login correctly
  if (url.host.startsWith('localhost:4000')) {
    url = urlWith(url, {
      host: 'localhost:3000',
      // TODO: Pass through originalTarget, do not overwrite
      search: paramsWith(url.search,
          { originalTarget: `http://${window.location.host}/app` }),
    });
  }
  return url.toString();
}

// Returns true iff `childId` is a descendent of `parentId` in the hierarchy described by `groups`
export function groupContains({ groups, childId, parentId }) {
  // Identity check
  if (childId === parentId) return true;

  // Abort early if we have no group definitions
  if (!groups) return false;

  // Recursive check up the tree
  let currentGroup = groups.find(g => g.id === childId);
  while (currentGroup?.parent) {
    if (currentGroup.parent === parentId) {
      return true;
    }
    currentGroup = groups.find(g => g.id === currentGroup.parent);
  }

  // Otherwise return false
  return false;
}
